class FacetFiltersForm extends HTMLElement {
    constructor() {
        super();
        this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

        this.debouncedOnSubmit = debounce((event) => {
            this.onSubmitHandler(event);
        }, 800);

        const facetForm = this.querySelector("form");
        facetForm.addEventListener("input", (event) => {
            if (this.dataset.defer === 'true') return;
            this.debouncedOnSubmit(event);
        });

        const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
        if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);
    }

    static setListeners() {
        const onHistoryChange = (event) => {
            const searchParams = event.state
                ? event.state.searchParams
                : FacetFiltersForm.searchParamsInitial;
            if (searchParams === FacetFiltersForm.searchParamsPrev) return;
            FacetFiltersForm.renderPage(searchParams, null, false);
        };
        window.addEventListener("popstate", onHistoryChange);
    }

    static toggleActiveFacets(disable = true) {
        document.querySelectorAll(".js-facet-remove").forEach((element) => {
            element.classList.toggle("disabled", disable);
        });
    }

    static renderPage(searchParams, event, updateURLHash = true) {
        FacetFiltersForm.searchParamsPrev = searchParams;
        const sections = FacetFiltersForm.getSections();
        const countContainer = document.getElementById("ProductCount");
        const countContainerDesktop = document.getElementById(
            "ProductCountDesktop"
        );
        const loadingSpinners = document.querySelectorAll(
            ".facets-container .loading__spinner, facet-filters-form .loading__spinner"
        );
        loadingSpinners.forEach((spinner) =>
            spinner.classList.remove("hidden")
        );
        document
            .getElementById("ProductGridContainer")
            .querySelector(".collection")
            .classList.add("loading");
        if (countContainer) {
            countContainer.classList.add("loading");
        }
        if (countContainerDesktop) {
            countContainerDesktop.classList.add("loading");
        }

        sections.forEach((section) => {
            const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
            const filterDataUrl = (element) => element.url === url;

            FacetFiltersForm.filterData.some(filterDataUrl)
                ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
                : FacetFiltersForm.renderSectionFromFetch(url, event);
        });

        if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    }

    static renderFiltersPreview(searchParams, event) {
        const sections = FacetFiltersForm.getSections();

        sections.forEach((section) => {
            const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
            const filterDataUrl = (element) => element.url === url;

            if (FacetFiltersForm.filterData.some(filterDataUrl)) {
                const html =
                    FacetFiltersForm.filterData.find(filterDataUrl).html;
                FacetFiltersForm.renderFilters(html, event);
                return;
            }

            fetch(url)
                .then((response) => response.text())
                .then((responseText) => {
                    const html = responseText;
                    FacetFiltersForm.filterData = [
                        ...FacetFiltersForm.filterData,
                        { html, url }
                    ];
                    FacetFiltersForm.renderFilters(html, event);
                });
        });
    }

    static renderSectionFromFetch(url, event) {
        fetch(url)
            .then((response) => response.text())
            .then((responseText) => {
                const html = responseText;
                FacetFiltersForm.filterData = [
                    ...FacetFiltersForm.filterData,
                    { html, url }
                ];
                FacetFiltersForm.renderFilters(html, event);
                FacetFiltersForm.renderProductGridContainer(html);
                FacetFiltersForm.renderProductCount(html);
                if (typeof initializeScrollAnimationTrigger === "function")
                    initializeScrollAnimationTrigger(html.innerHTML);
            });
    }

    static renderSectionFromCache(filterDataUrl, event) {
        const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === "function")
            initializeScrollAnimationTrigger(html.innerHTML);
    }

    static renderProductGridContainer(html) {
        document.getElementById("ProductGridContainer").innerHTML =
            new DOMParser()
                .parseFromString(html, "text/html")
                .getElementById("ProductGridContainer").innerHTML;

        document
            .getElementById("ProductGridContainer")
            .querySelectorAll(".scroll-trigger")
            .forEach((element) => {
                element.classList.add("scroll-trigger--cancel");
            });
    }

    static renderProductCount(html) {
        const parsed = new DOMParser().parseFromString(html, "text/html");
        const sourceCountEl =
            parsed.getElementById("ProductCount") ||
            parsed.getElementById("ProductCountDesktop");

        if (!sourceCountEl) {
            // Nothing to update but still hide any loading spinners to avoid them getting stuck
            const fallbackSpinners = document.querySelectorAll(
                ".facets-container .loading__spinner, facet-filters-form .loading__spinner"
            );
            fallbackSpinners.forEach((spinner) =>
                spinner.classList.add("hidden")
            );
            return;
        }

        const count = sourceCountEl.innerHTML;
        const container = document.getElementById("ProductCount");
        const containerDesktop = document.getElementById("ProductCountDesktop");

        if (container) {
            container.innerHTML = count;
            container.classList.remove("loading");
        }
        if (containerDesktop) {
            containerDesktop.innerHTML = count;
            containerDesktop.classList.remove("loading");
        }

        const loadingSpinners = document.querySelectorAll(
            ".facets-container .loading__spinner, facet-filters-form .loading__spinner"
        );
        loadingSpinners.forEach((spinner) => spinner.classList.add("hidden"));
    }

    static renderFilters(html, event) {
        const parsedHTML = new DOMParser().parseFromString(html, "text/html");
        const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
            "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter"
        );
        const facetDetailsElementsFromDom = document.querySelectorAll(
            "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter"
        );

        // Remove facets that are no longer returned from the server
        Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
            if (
                !Array.from(facetDetailsElementsFromFetch).some(
                    ({ id }) => currentElement.id === id
                )
            ) {
                currentElement.remove();
            }
        });

        const matchesId = (element) => {
            const jsFilter = event
                ? event.target.closest(".js-filter")
                : undefined;
            return jsFilter ? element.id === jsFilter.id : false;
        };

        const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter(
            (element) => !matchesId(element)
        );
        const countsToRender = Array.from(facetDetailsElementsFromFetch).find(
            matchesId
        );

        facetsToRender.forEach((elementToRender, index) => {
            const currentElement = document.getElementById(elementToRender.id);
            // Element already rendered in the DOM so just update the innerHTML
            if (currentElement) {
                document.getElementById(elementToRender.id).innerHTML =
                    elementToRender.innerHTML;
            } else {
                if (index > 0) {
                    const {
                        className: previousElementClassName,
                        id: previousElementId
                    } = facetsToRender[index - 1];
                    // Same facet type (eg horizontal/vertical or drawer/mobile)
                    if (
                        elementToRender.className === previousElementClassName
                    ) {
                        document
                            .getElementById(previousElementId)
                            .after(elementToRender);
                        return;
                    }
                }

                if (elementToRender.parentElement) {
                    document
                        .querySelector(
                            `#${elementToRender.parentElement.id} .js-filter`
                        )
                        .before(elementToRender);
                }
            }
        });

        FacetFiltersForm.renderActiveFacets(parsedHTML);
        FacetFiltersForm.renderAdditionalElements(parsedHTML);

        if (countsToRender) {
            const closestJSFilterID = event.target.closest(".js-filter").id;

            if (closestJSFilterID) {
                FacetFiltersForm.renderCounts(
                    countsToRender,
                    event.target.closest(".js-filter")
                );
                FacetFiltersForm.renderMobileCounts(
                    countsToRender,
                    document.getElementById(closestJSFilterID)
                );

                const newFacetDetailsElement =
                    document.getElementById(closestJSFilterID);
                const newElementSelector =
                    newFacetDetailsElement.classList.contains(
                        "mobile-facets__details"
                    )
                        ? `.mobile-facets__close-button`
                        : `.facets__summary`;
                const newElementToActivate =
                    newFacetDetailsElement.querySelector(newElementSelector);

                const isTextInput =
                    event.target.getAttribute("type") === "text";

                if (newElementToActivate && !isTextInput)
                    newElementToActivate.focus();
            }
        }

        if (typeof PseudoFilterSync !== "undefined") PseudoFilterSync.init();
    }

    static renderActiveFacets(html) {
        const activeFacetElementSelectors = [
            ".active-facets-mobile",
            ".active-facets-desktop"
        ];

        activeFacetElementSelectors.forEach((selector) => {
            const activeFacetsElement = html.querySelector(selector);
            if (!activeFacetsElement) return;
            document.querySelector(selector).innerHTML =
                activeFacetsElement.innerHTML;
        });

        FacetFiltersForm.toggleActiveFacets(false);
    }

    static renderAdditionalElements(html) {
        const mobileElementSelectors = [
            ".mobile-facets__open",
            ".mobile-facets__count",
            ".sorting"
        ];

        mobileElementSelectors.forEach((selector) => {
            if (!html.querySelector(selector)) return;
            document.querySelector(selector).innerHTML =
                html.querySelector(selector).innerHTML;
        });

        document
            .getElementById("FacetFiltersFormMobile")
            .closest("menu-drawer")
            .bindEvents();
    }

    static renderCounts(source, target) {
        const targetSummary = target.querySelector(".facets__summary");
        const sourceSummary = source.querySelector(".facets__summary");

        if (sourceSummary && targetSummary) {
            targetSummary.outerHTML = sourceSummary.outerHTML;
        }

        const targetHeaderElement = target.querySelector(".facets__header");
        const sourceHeaderElement = source.querySelector(".facets__header");

        if (sourceHeaderElement && targetHeaderElement) {
            targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
        }

        const targetWrapElement = target.querySelector(".facets-wrap");
        const sourceWrapElement = source.querySelector(".facets-wrap");

        if (sourceWrapElement && targetWrapElement) {
            const isShowingMore = Boolean(
                target.querySelector("show-more-button .label-show-more.hidden")
            );
            if (isShowingMore) {
                sourceWrapElement
                    .querySelectorAll(".facets__item.hidden")
                    .forEach((hiddenItem) =>
                        hiddenItem.classList.replace("hidden", "show-more-item")
                    );
            }

            targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
        }
    }

    static renderMobileCounts(source, target) {
        const targetFacetsList = target.querySelector(".mobile-facets__list");
        const sourceFacetsList = source.querySelector(".mobile-facets__list");

        if (sourceFacetsList && targetFacetsList) {
            targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
        }
    }

    static updateURLHash(searchParams) {
        history.pushState(
            { searchParams },
            "",
            `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
        );
    }

    static getSections() {
        return [
            {
                section: document.getElementById("product-grid").dataset.id
            }
        ];
    }

    createSearchParams(form) {
        const formData = new FormData(form);
        return new URLSearchParams(formData).toString();
    }

    getSearchParamsFromEvent(event) {
        const sortFilterForms = document.querySelectorAll(
            "facet-filters-form form"
        );
        const isMobileCheckbox =
            event.srcElement &&
            event.srcElement.className == "mobile-facets__checkbox";

        if (isMobileCheckbox) {
            return this.createSearchParams(event.target.closest("form"));
        }

        const forms = [];
        const targetForm = event.target.closest("form");
        if (!targetForm) return "";
        const isMobile = targetForm.id === "FacetFiltersFormMobile";

        sortFilterForms.forEach((form) => {
            if (!isMobile) {
                if (
                    form.id === "FacetSortForm" ||
                    form.id === "FacetFiltersForm" ||
                    form.id === "FacetSortDrawerForm"
                ) {
                    forms.push(this.createSearchParams(form));
                }
            } else if (form.id === "FacetFiltersFormMobile") {
                forms.push(this.createSearchParams(form));
            }
        });

        return forms.join("&");
    }

    onSubmitForm(searchParams, event) {
        FacetFiltersForm.renderPage(searchParams, event);
    }

    onFilterInputPreview(event) {
        const searchParams = this.getSearchParamsFromEvent(event);
    }

    onSubmitHandler(event) {
        event.preventDefault();
        const searchParams = this.getSearchParamsFromEvent(event);
        this.onSubmitForm(searchParams, event);
    }

    onActiveFilterClick(event) {
        event.preventDefault();
        FacetFiltersForm.toggleActiveFacets();
        const url =
            event.currentTarget.href.indexOf("?") == -1
                ? ""
                : event.currentTarget.href.slice(
                      event.currentTarget.href.indexOf("?") + 1
                  );
        FacetFiltersForm.renderPage(url);
    }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
    constructor() {
        super();
        this.querySelectorAll("input").forEach((element) => {
            element.addEventListener("change", this.onRangeChange.bind(this));
            element.addEventListener("keydown", this.onKeyDown.bind(this));
        });
        this.setMinAndMaxValues();
    }

    onRangeChange(event) {
        this.adjustToValidValues(event.currentTarget);
        this.setMinAndMaxValues();
    }

    onKeyDown(event) {
        if (event.metaKey) return;

        const pattern =
            /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
        if (!event.key.match(pattern)) event.preventDefault();
    }

    setMinAndMaxValues() {
        const inputs = this.querySelectorAll("input");
        const minInput = inputs[0];
        const maxInput = inputs[1];
        if (maxInput.value) minInput.setAttribute("data-max", maxInput.value);
        if (minInput.value) maxInput.setAttribute("data-min", minInput.value);
        if (minInput.value === "") maxInput.setAttribute("data-min", 0);
        if (maxInput.value === "")
            minInput.setAttribute(
                "data-max",
                maxInput.getAttribute("data-max")
            );
    }

    adjustToValidValues(input) {
        const value = Number(input.value);
        const min = Number(input.getAttribute("data-min"));
        const max = Number(input.getAttribute("data-max"));

        if (value < min) input.value = min;
        if (value > max) input.value = max;
    }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
    constructor() {
        super();
        const facetLink = this.querySelector("a");
        facetLink.setAttribute("role", "button");
        facetLink.addEventListener("click", this.closeFilter.bind(this));
        facetLink.addEventListener("keyup", (event) => {
            event.preventDefault();
            if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
        });
    }

    closeFilter(event) {
        event.preventDefault();
        const form =
            this.closest("facet-filters-form") ||
            document.querySelector("facet-filters-form");
        form.onActiveFilterClick(event);
    }
}

customElements.define("facet-remove", FacetRemove);

const PseudoFilterSync = (() => {
    const stateByRoot = new Map();

    function createEmptyState() {
        return {
            originalItems: [],
            locationTriggers: [],
            leadTimeTriggers: [],
            locationToLeadTimes: {},
            leadTimeToLocations: {},
            selectedLocations: new Set(),
            selectedLeadTimes: new Set()
        };
    }

    function init() {
        const roots = document.querySelectorAll("[data-pseudo-filter-root]");
        if (!roots.length) return;

        roots.forEach((root) => initRoot(root));
    }

    function initRoot(root) {
        let state = stateByRoot.get(root);
        if (!state) {
            state = createEmptyState();
            stateByRoot.set(root, state);
        }

        cacheDOM(root, state);
        if (!state.originalItems.length) return;
        buildFilterMap(root, state);
        bindPseudoClicks(root, state);
        syncFromOriginalCheckboxes(root, state);
        updateDisabledStates(root, state);
    }

    function cacheDOM(root, state) {
        state.originalItems = Array.from(
            root.querySelectorAll(
                "li[data-location-value][data-lead-time-value]"
            )
        );
        state.locationTriggers = Array.from(
            root.querySelectorAll("label[data-trigger-location]")
        );
        state.leadTimeTriggers = Array.from(
            root.querySelectorAll("label[data-trigger-lead-time]")
        );
    }

    function buildFilterMap(_root, state) {
        state.locationToLeadTimes = {};
        state.leadTimeToLocations = {};

        state.originalItems.forEach((li) => {
            const checkbox = li.querySelector('input[type="checkbox"]');
            if (!checkbox || checkbox.disabled) return;

            const loc = li.dataset.locationValue;
            const lead = li.dataset.leadTimeValue;
            if (!loc || !lead) return;

            if (!state.locationToLeadTimes[loc])
                state.locationToLeadTimes[loc] = new Set();
            state.locationToLeadTimes[loc].add(lead);

            if (!state.leadTimeToLocations[lead])
                state.leadTimeToLocations[lead] = new Set();
            state.leadTimeToLocations[lead].add(loc);
        });
    }

    function bindPseudoClicks(_root, state) {
        state.locationTriggers.forEach((label) => {
            label.removeEventListener("click", onLocationClick);
            label.addEventListener("click", onLocationClick);
        });

        state.leadTimeTriggers.forEach((label) => {
            label.removeEventListener("click", onLeadTimeClick);
            label.addEventListener("click", onLeadTimeClick);
        });
    }

    function onLocationClick(e) {
        e.preventDefault();
        const label = e.currentTarget;
        if (label.classList.contains("disabled")) return;

        const root = label.closest("[data-pseudo-filter-root]");
        if (!root) return;

        const state = stateByRoot.get(root);
        if (!state) return;

        const loc = label.dataset.triggerLocation;
        if (state.selectedLocations.has(loc)) {
            state.selectedLocations.delete(loc);
            if (state.selectedLocations.size === 0) {
                state.selectedLeadTimes.clear();
            }
        } else {
            state.selectedLocations.add(loc);

            if (state.selectedLeadTimes.size === 0) {
                const availableLeadTimes = state.locationToLeadTimes[loc];
                if (availableLeadTimes && availableLeadTimes.size > 0) {
                    const firstAvailableLeadTimeLabel = state.leadTimeTriggers.find(l => availableLeadTimes.has(l.dataset.triggerLeadTime));
                    if (firstAvailableLeadTimeLabel) {
                        state.selectedLeadTimes.add(firstAvailableLeadTimeLabel.dataset.triggerLeadTime);
                    }
                }
            }
        }

        updatePseudoActiveStates(root, state);
        updateDisabledStates(root, state);
        triggerOriginalCheckboxes(root, state);
    }

    function onLeadTimeClick(e) {
        e.preventDefault();
        const label = e.currentTarget;
        if (label.classList.contains("disabled")) return;

        const root = label.closest("[data-pseudo-filter-root]");
        if (!root) return;

        const state = stateByRoot.get(root);
        if (!state) return;

        const lead = label.dataset.triggerLeadTime;
        if (state.selectedLeadTimes.has(lead)) {
            state.selectedLeadTimes.delete(lead);
            if (state.selectedLeadTimes.size === 0) {
                state.selectedLocations.clear();
            }
                } else {
            state.selectedLeadTimes.clear();
            state.selectedLeadTimes.add(lead);

            if (state.selectedLocations.size === 0) {
                const availableLocations = state.leadTimeToLocations[lead];
                if (availableLocations && availableLocations.size > 0) {
                    const firstAvailableLocationLabel = state.locationTriggers.find(l => availableLocations.has(l.dataset.triggerLocation));
                    if (firstAvailableLocationLabel) {
                        state.selectedLocations.add(firstAvailableLocationLabel.dataset.triggerLocation);
                    }
                }
            }
        }

        updatePseudoActiveStates(root, state);
        updateDisabledStates(root, state);
        triggerOriginalCheckboxes(root, state);
    }

    function updatePseudoActiveStates(_root, state) {
        state.locationTriggers.forEach((label) => {
            label.classList.toggle(
                "active",
                state.selectedLocations.has(label.dataset.triggerLocation)
            );
        });

        state.leadTimeTriggers.forEach((label) => {
            label.classList.toggle(
                "active",
                state.selectedLeadTimes.has(label.dataset.triggerLeadTime)
            );
        });
    }

    function updateDisabledStates(_root, state) {
        if (
            state.selectedLocations.size === 0 &&
            state.selectedLeadTimes.size === 0
        ) {
            // Base state: disable pseudo options that have no enabled combinations at all
            state.locationTriggers.forEach((label) => {
                const loc = label.dataset.triggerLocation;
                const hasAny =
                    state.locationToLeadTimes[loc] &&
                    state.locationToLeadTimes[loc].size > 0;
                label.classList.toggle("disabled", !hasAny);
            });

            state.leadTimeTriggers.forEach((label) => {
                const lead = label.dataset.triggerLeadTime;
                const hasAny =
                    state.leadTimeToLocations[lead] &&
                    state.leadTimeToLocations[lead].size > 0;
                label.classList.toggle("disabled", !hasAny);
            });
            return;
        }

        if (state.selectedLocations.size > 0) {
            const availableLeadTimes = new Set();
            state.selectedLocations.forEach((loc) => {
                const leads = state.locationToLeadTimes[loc];
                if (leads) leads.forEach((l) => availableLeadTimes.add(l));
            });

            state.leadTimeTriggers.forEach((label) => {
                const lead = label.dataset.triggerLeadTime;
                label.classList.toggle(
                    "disabled",
                    !availableLeadTimes.has(lead)
                );
                if (
                    label.classList.contains("disabled") &&
                    state.selectedLeadTimes.has(lead)
                ) {
                    state.selectedLeadTimes.delete(lead);
                    label.classList.remove("active");
                }
            });
        } else {
            state.leadTimeTriggers.forEach((l) =>
                l.classList.remove("disabled")
            );
        }

        if (state.selectedLeadTimes.size > 0) {
            const availableLocations = new Set();
            state.selectedLeadTimes.forEach((lead) => {
                const locs = state.leadTimeToLocations[lead];
                if (locs) locs.forEach((l) => availableLocations.add(l));
            });

            state.locationTriggers.forEach((label) => {
                const loc = label.dataset.triggerLocation;
                label.classList.toggle(
                    "disabled",
                    !availableLocations.has(loc)
                );
                if (
                    label.classList.contains("disabled") &&
                    state.selectedLocations.has(loc)
                ) {
                    state.selectedLocations.delete(loc);
                    label.classList.remove("active");
                }
            });
        } else {
            state.locationTriggers.forEach((l) =>
                l.classList.remove("disabled")
            );
        }
    }

    function triggerOriginalCheckboxes(_root, state) {
        let anyClicked = false;

        state.originalItems.forEach((li) => {
            const loc = li.dataset.locationValue;
            const lead = li.dataset.leadTimeValue;
            const checkbox = li.querySelector('input[type="checkbox"]');
            if (!checkbox || checkbox.disabled) return;

            const shouldBeChecked =
                state.selectedLocations.has(loc) &&
                state.selectedLeadTimes.has(lead);

            if (checkbox.checked !== shouldBeChecked) {
                console.log("[PseudoFilterSync] Toggling original checkbox", {
                    loc,
                    lead,
                    shouldBeChecked
                });
                checkbox.click();
                anyClicked = true;
            }
        });

        if (!anyClicked) {
            console.log(
                "[PseudoFilterSync] No matching original checkbox to toggle",
                {
                    selectedLocations: Array.from(state.selectedLocations),
                    selectedLeadTimes: Array.from(state.selectedLeadTimes)
                }
            );
        }
    }

    function syncFromOriginalCheckboxes(root, state) {
        state.selectedLocations = new Set();
        state.selectedLeadTimes = new Set();

        if (root) {
            const activeLocs = root.dataset.activeLocations;
            const activeLeads = root.dataset.activeLeadTimes;

            if (activeLocs) {
                activeLocs.split("|||").forEach((v) => {
                    if (v) state.selectedLocations.add(v);
                });
            }
            if (activeLeads) {
                activeLeads.split("|||").forEach((v) => {
                    if (v) state.selectedLeadTimes.add(v);
                });
            }
        }

        if (
            state.selectedLocations.size === 0 &&
            state.selectedLeadTimes.size === 0
        ) {
            state.originalItems.forEach((li) => {
                const checkbox = li.querySelector('input[type="checkbox"]');
                if (checkbox && checkbox.checked) {
                    state.selectedLocations.add(li.dataset.locationValue);
                    state.selectedLeadTimes.add(li.dataset.leadTimeValue);
                }
            });
        }

        updatePseudoActiveStates(root, state);
    }

    return { init };
})();

document.addEventListener("DOMContentLoaded", () => PseudoFilterSync.init());
